/* Iter 418.am (Dirk, 2026-02-28) — Portal "Announcements" tab.
   Shows every announcement addressed to this portal user with
   acknowledgment status. Unread/unacknowledged pinned at the top.
*/
import { useEffect, useState } from "react";
import { Bell, CheckCircle2, Clock } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";

const PRIO = {
  info:     { color: "#22d3ee", label: "Info" },
  warn:     { color: "#f59e0b", label: "Warning" },
  critical: { color: "#ef4444", label: "Urgent" },
};

export default function PortalAnnouncements() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portal/me/announcements/history");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const ack = async (aid) => {
    setBusy(aid);
    try {
      await api.post(`/portal-announcements/${aid}/ack`);
      toast.success("Acknowledged");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Ack failed");
    } finally {
      setBusy(null);
    }
  };

  // Pending (not acked) first, then history.
  const pending = rows.filter((r) => r.require_ack !== false && !r.my_ack);
  const done = rows.filter((r) => !(r.require_ack !== false && !r.my_ack));

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4" data-testid="portal-announcements-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087]">Portal</div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2 mt-1">
          <Bell className="w-6 h-6 text-amber-400" /> Announcements
        </h1>
        <p className="text-sm text-[#a0aabf] mt-1">
          Notices from your HidroVerse team. Ones marked <em>Please acknowledge</em> need one tap to confirm you&rsquo;ve seen them.
        </p>
      </header>

      {err && <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">{err}</div>}
      {loading && <div className="hv-card p-5 text-sm text-[#647087]">Loading…</div>}

      {!loading && rows.length === 0 && !err && (
        <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="portal-announcements-empty">
          <Bell className="w-8 h-8 mx-auto text-[#22d3ee]/40 mb-2" />
          No announcements yet.
        </div>
      )}

      {pending.length > 0 && (
        <section className="space-y-2" data-testid="portal-announcements-pending">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">
            Needs your acknowledgment ({pending.length})
          </div>
          {pending.map((r) => <AnnouncementCard key={r.id} row={r} onAck={ack} busy={busy === r.id} pending />)}
        </section>
      )}

      {done.length > 0 && (
        <section className="space-y-2" data-testid="portal-announcements-history">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mt-4">
            History
          </div>
          {done.map((r) => <AnnouncementCard key={r.id} row={r} onAck={ack} busy={false} pending={false} />)}
        </section>
      )}
    </div>
  );
}

function AnnouncementCard({ row, onAck, busy, pending }) {
  const p = PRIO[row.priority] || PRIO.info;
  return (
    <div
      className="hv-card p-3 space-y-2"
      style={{ borderLeft: `3px solid ${p.color}` }}
      data-testid={`portal-announcement-card-${row.id}`}
    >
      <div className="flex items-start gap-2">
        <Bell className="w-4 h-4 mt-1 shrink-0" style={{ color: p.color }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ color: p.color, borderColor: `${p.color}66`, background: `${p.color}11` }}>
              {p.label}
            </span>
            <h3 className="text-sm font-semibold truncate">{row.title}</h3>
          </div>
          <div className="text-[10px] text-[#647087] mt-0.5 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {new Date(row.created_at).toLocaleString()}
            {row.created_by_name && <span>· by {row.created_by_name}</span>}
          </div>
        </div>
        {row.my_ack && (
          <span className="text-[10px] text-emerald-300 flex items-center gap-1 whitespace-nowrap" title={`Acknowledged ${new Date(row.my_ack.acked_at).toLocaleString()}`}>
            <CheckCircle2 className="w-3 h-3" /> Acknowledged
          </span>
        )}
      </div>
      <p className="text-sm text-slate-100 whitespace-pre-wrap pl-6">{row.body}</p>
      {pending && (
        <div className="flex justify-end pl-6 pt-1">
          <button
            type="button"
            onClick={() => onAck(row.id)}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs"
            data-testid={`portal-announcement-ack-${row.id}`}
          >
            {busy ? "Saving…" : "I acknowledge"}
          </button>
        </div>
      )}
    </div>
  );
}
